import { createContext, useContext } from "react";
import type { ReactNode } from "react";

/**
 * 위젯 i18n 라벨 컨텍스트.
 *
 * 서버(MCP)가 ``make_render_result`` 의 component 레지스트리(_helpers._COMPONENT_LABELS)
 * 로 display 표기언어(languageCode)를 i18n 해석해 payload ``labels`` 블록에 주입한다.
 * 프론트는 본 컨텍스트로 그 값을 받아 카드 트리 어디서든 ``useLabel`` 로 읽고,
 * 미주입(다른 도구가 렌더하는 경우 등) 시 영어 상수로 폴백한다.
 *
 * 단일 경로 — 카드별 prop drilling/평행 i18n 없이 여기서만 라벨을 해석한다.
 * i18n SOT: docs/domain/i18n.
 */
export type WidgetLabels = Record<string, string | undefined>;

const LabelsContext = createContext<WidgetLabels>({});

export function LabelsProvider({
  labels,
  children,
}: {
  labels: WidgetLabels | undefined;
  children: ReactNode;
}) {
  return (
    <LabelsContext.Provider value={labels || {}}>
      {children}
    </LabelsContext.Provider>
  );
}

/**
 * i18n 라벨 1건을 읽는다. 서버 주입값이 있으면 그 값을, 없으면 ``fallback``(영어 상수).
 *
 * @param key      payload ``labels`` 키 (서버 _COMPONENT_LABELS 키와 1:1).
 * @param fallback 영어 원문 상수 (i18n 미주입/미등록 시 폴백).
 */
export function useLabel(key: string, fallback: string): string {
  const labels = useContext(LabelsContext);
  const v = labels[key];
  return v != null && v !== "" ? v : fallback;
}

/** 라벨 맵 전체를 읽는다(동적 키 다수를 직접 조회할 때). */
export function useLabels(): WidgetLabels {
  return useContext(LabelsContext);
}
